import { z } from "zod";
import type { AssessmentModuleCode } from "../constants/modules.js";
import { trimmedString } from "./common.js";

/**
 * What a superadmin may write into `assessment_modules.config`, per module, and
 * the prompt versions that sit beside it.
 *
 * `config` is **what to ask**: the checklist, the capture window, the frame
 * budget. The prompt is **how to ask**. Both are editable from the console now,
 * but they are still two things, and the checklist never appears in a prompt -
 * the codes are templated into the user message at call time, so adding an item
 * is a config edit that the model picks up with no prompt change at all.
 */

/* ─────────────────────────────── checklists ─────────────────────────────── */

/**
 * A checklist code. The same rule `assessment_modules.code` uses, because both
 * are keys other rows point at.
 */
export const checklistCode = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]{1,39}$/,
    "Lowercase letters, digits and underscores, starting with a letter"
  );

export const checklistItemSchema = z.object({
  code: checklistCode,
  label: trimmedString(2, 60),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

/** Every detection row ever written carries one of these, so a list has a ceiling. */
export const MAX_CHECKLIST_ITEMS = 30;

/**
 * The checklist half every photo-checked module shares.
 *
 * **`retired` is where a removed item goes, and it is never emptied.** Detection
 * rows filed against `hard_hat` a year ago still say `hard_hat`, and both clients
 * label them from config. Deleting the entry would turn every one of those into
 * a raw code. Moving it here keeps the label while taking it out of `items`,
 * which is the only list an installed app reads when it builds a new check - so
 * retiring works on a phone that shipped before this key existed.
 *
 * `attested` names the codes a person confirms and a model is never asked -
 * `ground_firm` - and may only name live items.
 */
const checklistList = z.array(checklistItemSchema).min(1).max(MAX_CHECKLIST_ITEMS);
const checklistExtras = {
  attested: z.array(checklistCode).max(MAX_CHECKLIST_ITEMS).default([]),
  retired: z.array(checklistItemSchema).max(100).default([]),
};

/** Codes unique across live and retired, and `attested` only naming live ones. */
function checklistRefinements(key: "items" | "checks") {
  return (value: Record<string, unknown>, ctx: z.RefinementCtx) => {
    const live = (value[key] as ChecklistItem[]) ?? [];
    const retired = (value.retired as ChecklistItem[]) ?? [];
    const seen = new Set<string>();
    for (const item of [...live, ...retired]) {
      if (seen.has(item.code)) {
        ctx.addIssue({ code: "custom", path: [key], message: `"${item.code}" appears twice` });
      }
      seen.add(item.code);
    }
    const liveCodes = new Set(live.map((item) => item.code));
    for (const code of (value.attested as string[]) ?? []) {
      if (!liveCodes.has(code)) {
        ctx.addIssue({
          code: "custom",
          path: ["attested"],
          message: `"${code}" is not a live item`,
        });
      }
    }
  };
}

/* ──────────────────────────────── per module ─────────────────────────────── */

/**
 * The sweep, and how the phone turns it into key frames.
 *
 * The frame selection runs on the handset, because the gyroscope that says how
 * the camera moved only exists there. These are its thresholds - tuning them is
 * a config edit, and only a change to the algorithm itself needs a release.
 */
export const AREA_SCAN_DEFAULTS = {
  min_seconds: 20,
  max_seconds: 30,
  /** Hard cap. Azure takes at most ten images per request, so the schema does too. */
  max_frames: 8,
  /** Fewer usable frames than this and the phone asks for a better sweep. */
  min_frames: 3,
  /** Two frames pointing closer than this, that also look alike, are one view. */
  min_view_change_deg: 20,
  /** Faster than this and a moment is motion blur, not a candidate. Degrees per second. */
  max_turn_rate_dps: 60,
  /** Laplacian variance on the 320px grayscale candidate. Below it is soft. */
  min_sharpness: 60,
  /** Total camera travel below this is a phone pointed at one spot, not a sweep. */
  min_sweep_deg: 45,
} as const;

/** The Azure ceiling, and the reason `max_frames` stops at ten. docs/prompting.md §1. */
export const MAX_MODEL_IMAGES = 10;

export const areaScanConfigSchema = z
  .object({
    min_seconds: z.number().int().min(5).max(30).default(AREA_SCAN_DEFAULTS.min_seconds),
    // Thirty is a product constraint, not a default: the sweep is part of a
    // ninety-second budget, and the server reads no further into a clip.
    max_seconds: z.number().int().min(5).max(30).default(AREA_SCAN_DEFAULTS.max_seconds),
    max_frames: z
      .number()
      .int()
      .min(1)
      .max(MAX_MODEL_IMAGES)
      .default(AREA_SCAN_DEFAULTS.max_frames),
    min_frames: z
      .number()
      .int()
      .min(1)
      .max(MAX_MODEL_IMAGES)
      .default(AREA_SCAN_DEFAULTS.min_frames),
    min_view_change_deg: z.number().min(5).max(90).default(AREA_SCAN_DEFAULTS.min_view_change_deg),
    max_turn_rate_dps: z.number().min(10).max(360).default(AREA_SCAN_DEFAULTS.max_turn_rate_dps),
    min_sharpness: z.number().min(0).max(5000).default(AREA_SCAN_DEFAULTS.min_sharpness),
    min_sweep_deg: z.number().min(0).max(360).default(AREA_SCAN_DEFAULTS.min_sweep_deg),
  })
  .superRefine((value, ctx) => {
    if (value.min_seconds > value.max_seconds) {
      ctx.addIssue({ code: "custom", path: ["min_seconds"], message: "Longer than the maximum" });
    }
    if (value.min_frames > value.max_frames) {
      ctx.addIssue({ code: "custom", path: ["min_frames"], message: "More than the maximum" });
    }
  });
export type AreaScanConfig = z.infer<typeof areaScanConfigSchema>;

/**
 * The ladder's numbers. They have safe defaults, the same ones the phone falls
 * back to, so a row that predates a key still reads. **The checklist has none**:
 * `checks` is required, and a module without one fails closed on both clients
 * and the API rather than running a check that asks nothing.
 */
export const HEIGHTS_DEFAULTS = {
  target_angle_deg: 75.5,
  tolerance_deg: 2,
  auto_capture_band_deg: 5,
} as const;

export const heightsConfigSchema = z
  .object({
    checks: checklistList,
    ...checklistExtras,
    target_angle_deg: z.number().min(45).max(90).default(HEIGHTS_DEFAULTS.target_angle_deg),
    tolerance_deg: z.number().min(0.5).max(10).default(HEIGHTS_DEFAULTS.tolerance_deg),
    auto_capture_band_deg: z
      .number()
      .min(0.5)
      .max(20)
      .default(HEIGHTS_DEFAULTS.auto_capture_band_deg),
  })
  .superRefine((value, ctx) => {
    checklistRefinements("checks")(value, ctx);
    if (value.auto_capture_band_deg < value.tolerance_deg) {
      ctx.addIssue({
        code: "custom",
        path: ["auto_capture_band_deg"],
        message: "Narrower than the tolerance, so an in-tolerance ladder would not auto-capture",
      });
    }
  });
export type HeightsConfig = z.infer<typeof heightsConfigSchema>;

export const ppeConfigSchema = z
  .object({ items: checklistList, ...checklistExtras })
  .superRefine(checklistRefinements("items"));
export type PpeConfig = z.infer<typeof ppeConfigSchema>;

/**
 * At least one suggestion. The phone used to fall back to a copy compiled into
 * the app when the row had none, which hid the one catalogue row that had never
 * been set up - and made an empty list impossible to mean anything.
 */
export const utilityConfigSchema = z.object({
  suggestions: z.array(trimmedString(2, 60)).min(1, "Add at least one suggestion").max(20),
});
export type UtilityConfig = z.infer<typeof utilityConfigSchema>;

export const MODULE_CONFIG_SCHEMAS: Record<AssessmentModuleCode, z.ZodTypeAny> = {
  area_scan: areaScanConfigSchema,
  heights: heightsConfigSchema,
  ppe: ppeConfigSchema,
  utility: utilityConfigSchema,
};

/**
 * Whether a module's config holds everything the module needs to run.
 *
 * **Words fail closed; numbers have defaults.** A checklist, or utility's
 * suggestions, is what the check asks - the customer's decision, with no
 * sensible stand-in, so a row without one is not set up and every path that
 * would run it refuses: the phone will not open it, the API will not analyse
 * or save it. A threshold or a ladder angle has a safe default in the schema
 * above, so a row missing one still parses. Skipping still works either way.
 * This is the one definition of "set up", and the phone mirrors it.
 */
export function moduleConfigured(code: string, config: unknown): boolean {
  const schema = (MODULE_CONFIG_SCHEMAS as Record<string, z.ZodTypeAny | undefined>)[code];
  return schema ? schema.safeParse(config ?? {}).success : false;
}

/** Which key a module's checklist lives under, for the ones that have one. */
export const CHECKLIST_KEY: Partial<Record<AssessmentModuleCode, "items" | "checks">> = {
  ppe: "items",
  heights: "checks",
};

/**
 * Codes that existed before an edit and are missing after it.
 *
 * A code, once written, is referenced by detection rows forever, so an edit may
 * move it to `retired` but never drop it. The schemas above cannot see the
 * previous value; the API calls this with both.
 */
export function droppedChecklistCodes(
  code: AssessmentModuleCode,
  before: Record<string, unknown>,
  after: Record<string, unknown>
): string[] {
  const key = CHECKLIST_KEY[code];
  if (!key) return [];
  const codes = (config: Record<string, unknown>) =>
    new Set(
      [...((config[key] as ChecklistItem[]) ?? []), ...((config.retired as ChecklistItem[]) ?? [])]
        .filter((item) => item && typeof item.code === "string")
        .map((item) => item.code)
    );
  const next = codes(after);
  return [...codes(before)].filter((c) => !next.has(c));
}

/** `PATCH /modules/:code/config`. Re-parsed against the module's own schema. */
export const updateModuleConfigSchema = z.object({
  config: z.record(z.string(), z.unknown()),
});
export type UpdateModuleConfigInput = z.infer<typeof updateModuleConfigSchema>;

/* ──────────────────────────────── prompts ────────────────────────────────── */

/**
 * The modules a model reads, and so the ones with a prompt to edit. `utility`
 * records a prompt for the technician, not for a model - PRD §10.
 */
export const PROMPTED_MODULES = [
  "area_scan",
  "heights",
  "ppe",
] as const satisfies readonly AssessmentModuleCode[];
export type PromptedModuleCode = (typeof PROMPTED_MODULES)[number];

export function isPromptedModule(code: string): code is PromptedModuleCode {
  return (PROMPTED_MODULES as readonly string[]).includes(code);
}

/**
 * About 5,000 tokens, and the same ceiling as `prompt_versions.body`. Small
 * beside the frames sent with it. A prompt that needs more is usually restating
 * rules the response schema already carries on its fields.
 */
export const MAX_PROMPT_CHARS = 20_000;

/**
 * `POST /modules/:code/prompts` - save a new version.
 *
 * There is no edit. A version is what produced every answer that points at it,
 * so changing its text would change what those answers claim to have been asked.
 * Rolling back is activating an older one.
 */
export const createPromptVersionSchema = z.object({
  body: trimmedString(20, MAX_PROMPT_CHARS),
  note: trimmedString(1, 200).optional(),
  activate: z.boolean().default(true),
});
export type CreatePromptVersionInput = z.infer<typeof createPromptVersionSchema>;

export const moduleCodeParams = z.object({ code: z.string().regex(/^[a-z][a-z0-9_]{1,39}$/) });
export const promptVersionParams = moduleCodeParams.extend({ versionId: z.string().uuid() });

/** One version as the console lists it. */
export interface PromptVersion {
  id: string;
  version: number;
  body: string;
  note: string | null;
  created_at: string;
  created_by: { id: string; name: string | null } | null;
  /**
   * Eight hex of sha256(body + response schema), computed now rather than
   * stored - it is the suffix `assessment_attempts.model` carries for every
   * answer this version produced under the current schema.
   */
  hash: string;
  active: boolean;
}

/** `GET /modules` - the admin view of the catalogue. */
export interface AdminModule {
  id: string;
  code: string;
  label: string;
  blurb: string | null;
  advisory: boolean;
  is_active: boolean;
  config: Record<string, unknown>;
  updated_at: string;
  /** Null for a module no model reads, or one whose prompt was never seeded. */
  prompt: { version: number; id: string; hash: string } | null;
  prompted: boolean;
}
